import { Component, computed, inject, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BarGraphComponent } from '@shared/components/bar-graph/bar-graph.component';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import type { EChartsOption } from 'echarts';
import { IApplicationPerformanceKpis } from '../../admin-event-analytics.interface';
import { IBarDatum } from './admin-event-analytics-overview-bar-graphs.interface';

const BAR_COLOR = '#44084e'; // --shikenso-pink-200
const AXIS_LINE_COLOR = '#1f04241f'; // --shikenso-purple-base-12
const AXIS_LABEL_COLOR = '#1f042499'; // --shikenso-purple-base-60
const MS_PER_MINUTE = 60_000;
const TRANSLATION_KEY_PREFIX = 'pages.admin.eventAnalytics.overview';

// Our longest category labels (e.g. "Campaigns completed") need roughly this much horizontal
// room per category to render on a single line without touching their neighbours. Below that,
// we switch the label to wrap after the first word instead of letting it overlap.
const MIN_PX_PER_CATEGORY_FOR_SINGLE_LINE = 150;
// Target width for a wrapped (two-line) label - comfortably fits a single word from our labels.
const WRAPPED_LABEL_WIDTH_PX = 80;

/**
 * Owns all data shaping and echarts configuration for the Overview tab's two bar charts; the
 * `BarGraphComponent` it renders into is a "dumb" generic echarts wrapper with no knowledge of
 * campaigns, reports or durations.
 */
@Component({
  imports: [BarGraphComponent, TranslateModule],
  selector: 'app-admin-event-analytics-overview-bar-graphs',
  templateUrl: './admin-event-analytics-overview-bar-graphs.component.html',
})
export class AdminEventAnalyticsOverviewBarGraphsComponent {
  public readonly kpis = input<IApplicationPerformanceKpis | null>(null);
  public readonly loading = input<boolean>(false);

  public readonly journeyChartOptions = computed<EChartsOption>(() => this.buildBarChartOptions(this.journeyData()));

  public readonly timeOnTaskChartOptions = computed<EChartsOption>(() =>
    this.buildBarChartOptions(this.timeOnTaskData()),
  );

  private readonly translate = inject(TranslateService);
  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);
  private readonly durationUtilities = inject(DurationUtilitiesService);

  private readonly journeyData = computed<IBarDatum[]>(() => {
    const kpis = this.kpis();

    return [
      this.buildIntegerDatum('campaignsStarted', kpis?.campaignCreationsStarted ?? 0),
      this.buildIntegerDatum('campaignsCompleted', kpis?.campaignCreationsFinished ?? 0),
      this.buildIntegerDatum('reportsCreated', kpis?.reportsCreated ?? 0),
      this.buildIntegerDatum('briefingsCreated', kpis?.briefingsCreated ?? 0),
    ];
  });

  private readonly timeOnTaskData = computed<IBarDatum[]>(() => {
    const kpis = this.kpis();

    return [
      this.buildDurationDatum('finishCampaign', kpis?.avgTimeToCampaignCreationFinished ?? 0),
      this.buildDurationDatum('analyticsOverview', kpis?.avgTimeInAnalyticsPopupOverview ?? 0),
      this.buildDurationDatum('deliverableView', kpis?.avgTimeInAnalyticsPopupDeliverableView ?? 0),
    ];
  });

  private buildIntegerDatum(key: string, value: number): IBarDatum {
    return {
      category: this.translateKey(`journeyChart.${key}Label`),
      plottedValue: value,
      rawValue: value,
      tooltipLabel: this.translateKey(`journeyChart.${key}TooltipLabel`),
      tooltipTitle: this.translateKey(`journeyChart.${key}Title`),
      valueType: 'integer',
    };
  }

  private buildDurationDatum(key: string, valueMs: number): IBarDatum {
    return {
      category: this.translateKey(`timeChart.${key}Label`),
      plottedValue: valueMs / MS_PER_MINUTE,
      rawValue: valueMs,
      tooltipLabel: this.translateKey('timeChart.avgTimeTooltipLabel'),
      tooltipTitle: this.translateKey(`timeChart.${key}Title`),
      valueType: 'duration',
    };
  }

  private translateKey(key: string): string {
    return this.translate.instant(`${TRANSLATION_KEY_PREFIX}.${key}`);
  }

  private buildBarChartOptions(data: IBarDatum[]): EChartsOption {
    // echarts re-evaluates `media` against the chart's own rendered pixel width (not the window)
    // every time it resizes, and re-merges it on top of `baseOption` from scratch each time - so
    // this alone gives us "wrap only once the chart itself gets too narrow", with no manual
    // resize listening on our side. Below the breakpoint, labels wrap after the first word
    // (`overflow: 'break'` only inserts a line break where text would otherwise exceed `width`);
    // above it, no override is applied and labels render on a single line as before.
    const narrowBreakpoint = data.length * MIN_PX_PER_CATEGORY_FOR_SINGLE_LINE;
    const isDurationChart = data.length > 0 && data.every((datum) => datum.valueType === 'duration');

    const baseOption: EChartsOption = {
      backgroundColor: 'transparent',
      grid: { bottom: 36, containLabel: true, left: 8, right: 16, top: 16 },
      series: [
        {
          barMaxWidth: 56,
          data: data.map((datum) => datum.plottedValue),
          itemStyle: { borderRadius: [6, 6, 0, 0], color: BAR_COLOR },
          type: 'bar',
        },
      ],
      tooltip: {
        borderWidth: 1,
        confine: true,
        formatter: (params: unknown) => this.formatTooltip(params, data),
        trigger: 'item',
      },
      xAxis: {
        // interval: 0 forces every category label to render - echarts' default 'auto' interval
        // hides labels it thinks would overlap, which was silently dropping some of ours.
        axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 12, interval: 0 },
        axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
        axisTick: { show: false },
        data: data.map((datum) => datum.category),
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          color: AXIS_LABEL_COLOR,
          fontSize: 12,
          // `value` here is a tick on the plotted axis (minutes - see `buildDurationDatum`), not
          // milliseconds, so it's converted back before going through the same duration formatter
          // the tooltip uses, keeping "30s" / "2m 21s"-style labels consistent between the two.
          formatter: isDurationChart
            ? (value: number): string => this.durationUtilities.getDuration(value * MS_PER_MINUTE, 'milliseconds')
            : undefined,
        },
        // The journey chart's values (campaign/report/briefing counts) are always whole numbers,
        // so echarts' auto-picked ticks should never land on a fractional step like "0.5"/"1.5" -
        // `minInterval: 1` floors the step size at a whole unit. The duration chart's ticks are
        // fine at any fractional-minute step, since they're always rendered through the duration
        // formatter above rather than shown as a raw number.
        minInterval: isDurationChart ? undefined : 1,
        splitLine: { lineStyle: { color: AXIS_LINE_COLOR } },
        type: 'value',
      },
    };

    return {
      baseOption,
      media: [
        {
          option: {
            xAxis: {
              axisLabel: { lineHeight: 14, overflow: 'break', width: WRAPPED_LABEL_WIDTH_PX },
            },
          },
          query: { maxWidth: narrowBreakpoint },
        },
      ],
    };
  }

  private formatTooltip(params: unknown, data: IBarDatum[]): string {
    const dataIndex = (params as { dataIndex?: number } | undefined)?.dataIndex;
    const datum = typeof dataIndex === 'number' ? data[dataIndex] : undefined;

    if (!datum) {
      return '';
    }

    const formattedValue =
      datum.valueType === 'duration'
        ? this.durationUtilities.getDuration(datum.rawValue, 'milliseconds')
        : this.numberSeparatorUtilities.formatNumberToLocaleString(datum.rawValue, null, 0, 0, true);

    return [
      `<div style="font-weight:600;margin-bottom:4px;">${datum.tooltipTitle}</div>`,
      '<div style="display:flex;align-items:center;gap:6px;">',
      `<span style="display:inline-block;width:8px;height:8px;border-radius:9999px;background:${BAR_COLOR};"></span>`,
      `<span>${datum.tooltipLabel} — ${formattedValue}</span>`,
      '</div>',
    ].join('');
  }
}
